/**
 * Create-influencer modal for the dashboard. On success returns the new profile so the
 * caller can patch the cache (no refetch).
 */
import React, { useState } from 'react'
import {
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Input,
    Textarea,
    Select,
    Option,
    FormControl,
    FormLabel,
    Alert,
    Stack,
} from '@mui/joy'
import { SubscriptionPlan } from '@numeo/types-core'
import { isAxiosError } from 'axios'
import { billingApi } from './api'
import { InfluencerProfile, InfluencerTrialPlan } from './types'

const TIERS = ['Silver', 'Gold', 'Platinum']

interface Props {
    open: boolean
    onClose: () => void
    onCreated: (influencer: InfluencerProfile) => void
}

export default function InfluencerCreateModal({ open, onClose, onCreated }: Props) {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [platform, setPlatform] = useState('')
    const [tier, setTier] = useState('Silver')
    const [commissionPercent, setCommissionPercent] = useState('25')
    const [notes, setNotes] = useState('')
    const [password, setPassword] = useState('')
    const [trialDays, setTrialDays] = useState('30')
    const [trialPlan, setTrialPlan] = useState<InfluencerTrialPlan>(SubscriptionPlan.PRO)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const reset = () => {
        setName('')
        setEmail('')
        setPlatform('')
        setTier('Silver')
        setCommissionPercent('25')
        setNotes('')
        setPassword('')
        setTrialDays('30')
        setTrialPlan(SubscriptionPlan.PRO)
        setError(null)
    }

    const handleClose = () => {
        if (saving) return
        reset()
        onClose()
    }

    const handleCreate = async () => {
        if (!name.trim()) {
            setError('Name is required')
            return
        }
        const pct = Number(commissionPercent)
        if (Number.isNaN(pct) || pct < 0 || pct > 100) {
            setError('Commission must be between 0 and 100')
            return
        }
        const days = Number(trialDays)
        if (!Number.isInteger(days) || days < 1 || days > 365) {
            setError('Trial length must be between 1 and 365 days')
            return
        }
        try {
            setSaving(true)
            setError(null)
            const created = await billingApi.createInfluencer({
                name: name.trim(),
                email: email.trim() || undefined,
                platform: platform.trim() || undefined,
                tier,
                commissionPercent: pct,
                notes: notes.trim() || undefined,
                password: password.trim() || undefined,
                trialDays: days,
                trialPlan,
            })
            onCreated(created)
            reset()
            onClose()
        } catch (err: unknown) {
            setError((isAxiosError<{ message?: string }>(err) && err.response?.data?.message) || 'Failed to create influencer')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalDialog variant="outlined" sx={{ maxWidth: 460, width: '100%' }}>
                <DialogTitle>New Influencer</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                        {error && (
                            <Alert color="danger" variant="soft">
                                {error}
                            </Alert>
                        )}
                        <FormControl required>
                            <FormLabel>Name</FormLabel>
                            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={saving} autoFocus />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Email</FormLabel>
                            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={saving} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Platform</FormLabel>
                            <Input placeholder="YouTube, TikTok, …" value={platform} onChange={(e) => setPlatform(e.target.value)} disabled={saving} />
                        </FormControl>
                        <Stack direction="row" spacing={1.5}>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Tier</FormLabel>
                                <Select value={tier} onChange={(_, v) => setTier(v || 'Silver')} disabled={saving}>
                                    {TIERS.map((t) => (
                                        <Option key={t} value={t}>
                                            {t}
                                        </Option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Commission %</FormLabel>
                                <Input
                                    type="number"
                                    slotProps={{ input: { min: 0, max: 100 } }}
                                    value={commissionPercent}
                                    onChange={(e) => setCommissionPercent(e.target.value)}
                                    disabled={saving}
                                />
                            </FormControl>
                        </Stack>
                        <Stack direction="row" spacing={1.5}>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Trial length (days)</FormLabel>
                                <Input
                                    type="number"
                                    slotProps={{ input: { min: 1, max: 365 } }}
                                    value={trialDays}
                                    onChange={(e) => setTrialDays(e.target.value)}
                                    disabled={saving}
                                />
                            </FormControl>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Trial plan</FormLabel>
                                <Select value={trialPlan} onChange={(_, v) => v && setTrialPlan(v)} disabled={saving}>
                                    <Option value={SubscriptionPlan.PRO}>Pro</Option>
                                    <Option value={SubscriptionPlan.ULTRA}>Ultra</Option>
                                </Select>
                            </FormControl>
                        </Stack>
                        <FormControl>
                            <FormLabel>Portal Password</FormLabel>
                            <Input
                                type="password"
                                placeholder="Set to enable portal access (needs email)"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={saving}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Notes</FormLabel>
                            <Textarea minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={saving} />
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="plain" color="neutral" onClick={handleClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="solid" loading={saving} onClick={handleCreate}>
                        Create
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    )
}
